import { Component, input, signal, type OnInit } from '@angular/core';
import { DateTime } from 'luxon';
import { EsdevenimentDTO } from 'src/app/DTO/EsdevenimentDTO';
import { TicketPriceDTO } from 'src/app/DTO/TicketPriceDTO';
import { CommunityService } from 'src/app/services/community/community.service';
import { EventCardDTO } from 'src/app/DTO/EventCardDTO';
import { TicketMinDTO } from 'src/app/DTO/min/TicketMinDTO';
import { TicketsGroupDTO } from 'src/app/DTO/tickets/TicketsGroupDTO';

@Component({
  selector: 'app-event-card',
  templateUrl: './event-card.component.html',
  styleUrls: ['./event-card.component.scss'],
})
export class EventCardComponent implements OnInit {

  ticket = input<TicketMinDTO>(new TicketMinDTO());
  event = input<EsdevenimentDTO>(new EsdevenimentDTO());
  isCustomCommunity = input<boolean>(false);

  public prices: TicketPriceDTO[];
  public isFree: boolean;
  public sameDay: boolean;
  public duration: number;
  public defaultImg: string;
  public model: EventCardDTO;
  public buttonText: string;
  readonly displayDate = signal<Date | null>(null);

  private start: DateTime;
  private end: DateTime;

  constructor(private communityService: CommunityService) { }

  ngOnInit(): void {
    this.defaultImg = this.communityService.defaultImg;

    if(this.ticket().id) {
      this.setTicketCard();
    } else if(this.event) {
      this.setEventCard();
    }
  }

  private getTicketDisplayDates(ticket: TicketMinDTO): { from: Date | null; to: Date | null } {
    if (ticket.eventDateFrom) {
      return { from: this.toDate(ticket.eventDateFrom), to: this.toDate(ticket.eventDateTo) };
    }

    if (ticket.startEvent) {
      return { from: this.toDate(ticket.startEvent), to: this.toDate(ticket.endEvent) };
    }

    return this.getSessionDateRange(ticket.ticketsGroups);
  }

  private getSessionDateRange(groups?: TicketsGroupDTO[] | null): { from: Date | null; to: Date | null } {
    const parsed = (groups ?? [])
      .map(group => {
        const start = this.toDate(group.startTime);
        const end = this.toDate(group.endTime) ?? start;
        return start ? { start, end } : null;
      })
      .filter((item): item is { start: Date; end: Date } => item != null);

    if (!parsed.length) {
      return { from: null, to: null };
    }

    parsed.sort((a, b) => a.start.getTime() - b.start.getTime());
    const lastEnd = parsed.reduce(
      (latest, item) => item.end.getTime() > latest.getTime() ? item.end : latest,
      parsed[0].end,
    );

    return { from: parsed[0].start, to: lastEnd };
  }

  private toDate(value?: string | Date | null): Date | null {
    if (value == null || value === '') {
      return null;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private setTicketCard(): void {
    const ticket = this.ticket();
    const {
      images,
      eventImage,
      saleDateFrom,
      saleDateTo,
      title,
      id,
      prices,
      categoryName,
      categoryId,
      isOpened
    } = ticket;

    const saleStart = DateTime.fromISO(saleDateFrom);
    const saleEnd = DateTime.fromISO(saleDateTo);
    const displayDates = this.getTicketDisplayDates(ticket);
    const displayDate = displayDates.from ?? saleStart.toJSDate();
    this.displayDate.set(displayDate);

    this.start = DateTime.fromJSDate(displayDate);
    this.end = displayDates.to
      ? DateTime.fromJSDate(displayDates.to)
      : (displayDates.from ? this.start : saleEnd);

    this.model = {
      image: !images || images.length === 0 ? eventImage : images[0].path,
      name: title,
      ticketId: id,
      allDay: false,
      categoryId,
      categoryName,
      dateFrom: this.start.toJSDate(),
      dateTo: this.end.toJSDate(),
      disabled: !isOpened
    };

    if(!isOpened) {
      const now = DateTime.now();

      if (saleStart?.isValid && now <= saleStart) {
        this.buttonText = 'Programmed';
      }

      if (saleEnd?.isValid && now >= saleEnd) {
        this.buttonText = 'Ended';
      }
    }

    if (displayDates.from && (!displayDates.to || this.start.hasSame(this.end, 'day'))) {
      this.sameDay = true;
    } else if (this.end) {
      this.checkIsSameDay();
    }

    this.duration = this.getDuration();
    this.isFree = this.getIsFree(prices);
  }

  private setEventCard(): void {
    const {
      dateFrom,
      dateTo,
      ticketPrices,
      name,
      image,
      allDay,
      categoryId,
      categoryName
    } = this.event();

    this.start = DateTime.fromJSDate(dateFrom);
    this.end = DateTime.fromJSDate(dateTo);
    this.displayDate.set(dateFrom);

    this.model = {
      image,
      name,
      dateFrom,
      dateTo,
      allDay,
      categoryName,
      categoryId,
      disabled: false
    };

    if(dateTo) {
      this.checkIsSameDay();
    }

    this.isFree = this.getIsFree(ticketPrices);
  }

  private getDuration(): number {
    if (!this.start?.isValid || !this.end?.isValid) {
      return null;
    }
    const diff = this.end.diff(this.start).as('days');
    return Math.trunc(diff);
  }

  private getIsFree(prices: TicketPriceDTO[]): boolean {
    return prices.every(x => x.totalWithFee == 0);
  }

  private checkIsSameDay(): void {
    this.sameDay = this.start.hasSame(this.end, 'day');

    if (!this.sameDay) {
      this.duration = this.getDuration();
    }
  }
}
